/**
 * Booking Progress Indicator
 *
 * Displays 7-stage booking progress with desktop and mobile layouts
 * Matches visual style of Guide Signup Flow progress indicator
 *
 * Location: src/components/booking/flow/BookingProgressIndicator.jsx
 * Epic: MVP 1.4 - Customer Booking Flow
 *
 * Features:
 * - Desktop: Horizontal layout with stage circles and connector lines
 * - Mobile: Scrollable horizontal with current stage prominent
 * - Clickable stages (for completed stages only)
 * - Visual feedback (completed, current, upcoming)
 */

import React from 'react';

const STAGES = [
  { num: 1, name: 'Trip Details', sublabel: 'Date & guests' },
  { num: 2, name: 'Account', sublabel: 'Create or sign in' },
  { num: 3, name: 'Contact', sublabel: 'Stay connected' },
  { num: 4, name: 'Requirements', sublabel: 'Age & needs' },
  { num: 5, name: 'Review', sublabel: 'Confirm details' },
  { num: 6, name: 'Terms', sublabel: 'Policies' },
  { num: 7, name: 'Payment', sublabel: 'Complete booking' }
];

/**
 * BookingProgressIndicator Component
 *
 * @param {Object} props
 * @param {number} props.currentStage - Current stage number (1-7)
 * @param {number} props.highestCompletedStage - Highest completed stage (0-7)
 * @param {Function} props.onStageClick - Handler for stage click
 * @returns {JSX.Element}
 */
const BookingProgressIndicator = ({
  currentStage = 1,
  highestCompletedStage = 0,
  onStageClick
}) => {
  const handleStageClick = (stageNum) => {
    // Allow clicking any completed stage OR any stage up to current stage
    // (When editing from a later stage, you should be able to navigate back to that stage)
    const maxAccessibleStage = Math.max(highestCompletedStage, currentStage);
    if (stageNum <= maxAccessibleStage && stageNum !== currentStage && onStageClick) {
      onStageClick(stageNum);
    }
  };

  return (
    <div className="bg-gray-50 border-b">
      {/* Desktop: Full horizontal layout */}
      <div className="hidden md:block py-6 px-4">
        <div className="flex items-start justify-between max-w-4xl mx-auto">
          {STAGES.map((stage, idx) => {
            const maxAccessibleStage = Math.max(highestCompletedStage, currentStage);
            const isCompleted = stage.num <= highestCompletedStage;
            const isCurrent = stage.num === currentStage;
            const isClickable = stage.num <= maxAccessibleStage && stage.num !== currentStage;

            return (
              <React.Fragment key={stage.num}>
                {/* Stage circle + label */}
                <div className="flex flex-col items-center" style={{ width: '100px' }}>
                  <button
                    onClick={() => handleStageClick(stage.num)}
                    disabled={!isClickable}
                    aria-label={`${stage.name} - Stage ${stage.num}`}
                    aria-current={isCurrent ? 'step' : undefined}
                    className={`
                      w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold
                      transition-all duration-200 border-2
                      ${isCompleted
                        ? 'bg-emerald-500 border-emerald-500 text-white'
                        : isCurrent
                          ? 'bg-white border-emerald-500 text-emerald-500'
                          : 'bg-white border-gray-300 text-gray-400'}
                      ${isClickable ? 'hover:bg-emerald-600 hover:text-white cursor-pointer' : ''}
                      ${!isClickable && !isCurrent ? 'cursor-not-allowed' : ''}
                    `}
                  >
                    {stage.num}
                  </button>
                  <span className={`mt-2 text-xs font-medium text-center ${isCompleted || isCurrent || isClickable ? 'text-emerald-600' : 'text-gray-500'}`}>
                    {stage.name}
                  </span>
                  <span className="text-xs text-gray-400 text-center">
                    {stage.sublabel}
                  </span>
                </div>

                {/* Connector line */}
                {idx < STAGES.length - 1 && (
                  <div className="flex-1 flex items-center pt-5">
                    <div className={`h-0.5 w-full ${stage.num < currentStage ? 'bg-emerald-500' : 'bg-gray-200'}`} />
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* Mobile: Scrollable horizontal with current stage prominent */}
      <div className="md:hidden">
        {/* Current stage header */}
        <div className="px-4 py-3 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-500">Stage {currentStage} of 7</span>
            <span className="text-sm font-medium text-emerald-600">{STAGES[currentStage - 1].name}</span>
          </div>
        </div>

        {/* Scrollable stage indicators */}
        <div className="overflow-x-auto py-4 px-2">
          <div className="flex items-center gap-1" style={{ minWidth: 'max-content', paddingLeft: '8px', paddingRight: '8px' }}>
            {STAGES.map((stage, idx) => {
              const maxAccessibleStage = Math.max(highestCompletedStage, currentStage);
              const isCompleted = stage.num <= highestCompletedStage;
              const isCurrent = stage.num === currentStage;
              const isClickable = stage.num <= maxAccessibleStage && stage.num !== currentStage;

              return (
                <React.Fragment key={stage.num}>
                  <button
                    onClick={() => handleStageClick(stage.num)}
                    disabled={!isClickable}
                    aria-label={`${stage.name} - Stage ${stage.num}`}
                    aria-current={isCurrent ? 'step' : undefined}
                    className={`
                      flex flex-col items-center min-w-[60px] px-2 py-1 rounded-lg
                      ${isCurrent ? 'bg-emerald-50' : ''}
                      ${!isClickable && !isCurrent ? 'opacity-50' : ''}
                    `}
                  >
                    <div className={`
                      w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold
                      transition-all border-2
                      ${isCompleted
                        ? 'bg-emerald-500 border-emerald-500 text-white'
                        : isCurrent
                          ? 'bg-white border-emerald-500 text-emerald-500'
                          : 'bg-white border-gray-300 text-gray-400'}
                    `}>
                      {stage.num}
                    </div>
                    <span className={`text-xs mt-1 whitespace-nowrap ${isCurrent ? 'text-emerald-600 font-medium' : isCompleted || isClickable ? 'text-emerald-600' : 'text-gray-400'}`}>
                      {stage.name}
                    </span>
                  </button>

                  {idx < STAGES.length - 1 && (
                    <div className={`w-4 h-0.5 ${stage.num < currentStage ? 'bg-emerald-500' : 'bg-gray-200'}`} />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingProgressIndicator;
